import { invariant } from '@seen/core/invariant'
import type { Participant } from '@seen/core/models/patients/participant'
import { ExtendedQuestionnaireResponse } from '@seen/core/models/questionnaireResponse'
import * as questionnaires from '@seen/core/models/questionnaires/index'
import { DotSeparator, TypographyDetail, TypographySubtle } from '@seen/ui'
import type { JSX } from 'react'
import { Avatar } from '#src/components/core/Avatar/Avatar.tsx'
import { CopyButton } from '#src/components/core/CopyButton.tsx'
import { AddressDisplay } from '#src/components/patient/detail-content/contact-tab/AddressDisplay.tsx'
import { DisenrollmentDateDisplay } from '#src/components/patient/detail-content/contact-tab/DisenrollmentDate.tsx'
import { EditButton } from '#src/components/patient/detail-content/contact-tab/EditButton.tsx'
import { EnrollmentDateDisplay } from '#src/components/patient/detail-content/contact-tab/EnrollmentDateDisplay.tsx'
import { GenderDisplay } from '#src/components/patient/detail-content/contact-tab/GenderDisplay.tsx'
import { LanguagesDisplay } from '#src/components/patient/detail-content/contact-tab/LanguagesDisplay.tsx'
import { NameDisplay } from '#src/components/patient/detail-content/contact-tab/NameDisplay.tsx'
import { PhoneDisplay } from '#src/components/patient/detail-content/contact-tab/PhoneDisplay.tsx'
import {
  FacesheetCard,
  FacesheetCardCollapseTrigger,
  FacesheetCardContent,
  FacesheetCardHeader,
  FacesheetCardIcon,
  FacesheetCardLabel,
  FacesheetCardTitle,
} from '#src/components/patient/detail-content/facesheet/index.ts'
import { QuestionnaireItemForm } from '#src/components/questionnaire-form/questionnaire-item-form/QuestionnaireItemForm.tsx'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { useSearchOneOrCreate } from '#src/hooks/useSearchOneOrCreate.ts'

export interface ParticipantCardProps {
  participant: Participant
}

export function ParticipantCard({
  participant,
}: ParticipantCardProps): JSX.Element {
  const notesItem = questionnaires.AdditionalInformationQuestionnaire.item.find(
    (i) => i.linkId === 'contact-note'
  )
  invariant(
    notesItem,
    'Contact note item not found in Additional Information Questionnaire'
  )

  const questionnaireResponseResult = useSearchOneOrCreate(
    ExtendedQuestionnaireResponse.searchQuery({
      questionnaire: questionnaires.AdditionalInformationQuestionnaire.url,
      subject: participant.reference,
    }),
    ExtendedQuestionnaireResponse.buildFromQuestionnaire({
      questionnaire: questionnaires.AdditionalInformationQuestionnaire,
      subject: participant.reference,
    })
  )
  const questionnaireResponse = questionnaireResponseResult.data ?? null

  const isReadOnly = useIsReadOnly()
  const profile = participant.toFhirResource()

  return (
    <FacesheetCard rumName="participant">
      <FacesheetCardHeader>
        <FacesheetCardTitle>
          <FacesheetCardIcon asChild>
            <Avatar profile={profile} size="icon" />
          </FacesheetCardIcon>
          <FacesheetCardLabel>{participant.name}</FacesheetCardLabel>
          <FacesheetCardCollapseTrigger />
        </FacesheetCardTitle>
      </FacesheetCardHeader>
      <FacesheetCardContent>
        <QuestionnaireItemForm
          item={notesItem}
          label={notesItem.text}
          questionnaireResponse={questionnaireResponse}
        />
        <NameDisplay name={participant.name} profile={profile} />
        <GenderDisplay profile={participant} />
        <LanguagesDisplay profile={participant} />
        <PhoneDisplay
          phoneNumber={participant.primaryPhoneNumber}
          profile={profile}
        />
        <AddressDisplay instance={participant} profile={profile} />
        <div className="group flex items-center gap-1">
          <div className="grid flex-1">
            <TypographyDetail className="leading-none font-semibold text-zinc-950">
              Birth Date
            </TypographyDetail>
            <TypographySubtle className="py-1.5">
              {participant.birthDate ? (
                <>
                  {participant.birthDateFormatted}
                  <DotSeparator />
                  {participant.age}
                </>
              ) : (
                'Unknown'
              )}
            </TypographySubtle>
          </div>
          {!isReadOnly && (
            <div className="invisible flex group-hover:visible">
              <EditButton profile={profile} field="birthDate" />
            </div>
          )}
        </div>
        <div className="group flex items-center gap-1">
          <div className="grid flex-1">
            <TypographyDetail className="leading-none font-semibold text-zinc-950">
              MRN
            </TypographyDetail>
            <TypographySubtle className="py-1.5">
              {participant.seenMRN ?? 'Unknown'}
            </TypographySubtle>
          </div>
          <div className="invisible flex group-hover:visible">
            <CopyButton value={participant.seenMRN} ariaLabel="Copy MRN" />
          </div>
        </div>
        <EnrollmentDateDisplay participant={participant} />
        <DisenrollmentDateDisplay participant={participant} />
      </FacesheetCardContent>
    </FacesheetCard>
  )
}
